import { Link } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import {
  Ban,
  Check,
  ExternalLink,
  Loader2,
  Mail,
  Paperclip,
  RefreshCw,
  User,
} from "lucide-react";
import { useAuthStore } from "@/store/auth";
import { formatDateTime } from "@/utils/formatDate";
import { AronMarkdown } from "@/features/aron/components/AronMarkdown";
import { AronModelPicker } from "@/features/aron/components/AronModelPicker";
import { AronToolStep } from "@/features/aron/components/AronToolStep";
import { AronWordmark } from "@/features/aron/components/AronMark";
import { AronWriteProposal } from "@/features/aron/components/AronWriteProposal";
import type { AronMessage, AronPendingWrite } from "@/features/aron/types";
import { aronMailKeys, useCancelEmail, useReprocessEmail } from "../api/aronMailApi";
import type { InboundEmailDetail, InboundEmailStatus } from "../types";
import { AronMailBody } from "./AronMailBody";
import { AronMailStatusBadge } from "./AronMailStatusBadge";
import { AronMailWriteCard } from "./AronMailWriteCard";

const REPROCESSABLE: InboundEmailStatus[] = [
  "unmatched",
  "error",
  "reply_failed",
  "budget_deferred",
  "cancelled",
];

function lastAssistantMessage(messages: AronMessage[]): AronMessage | null {
  const done = messages.filter((m) => m.role === "assistant" && m.text);
  return done.length ? done[done.length - 1] : null;
}

/** The assistant turn currently being generated — the last assistant row, which
 * while processing is `pending`/`streaming` and carries the live tool steps. */
function inProgressAssistant(messages: AronMessage[]): AronMessage | null {
  const assistant = messages.filter((m) => m.role === "assistant");
  return assistant.length ? assistant[assistant.length - 1] : null;
}

function collectWrites(messages: AronMessage[]): AronPendingWrite[] {
  return messages.flatMap((m) => m.pending_writes ?? []);
}

export function AronMailDetail({ email }: { email: InboundEmailDetail }) {
  const user = useAuthStore((s) => s.user);
  const qc = useQueryClient();
  const reprocess = useReprocessEmail();
  const cancel = useCancelEmail();
  const isOwner = !!email.matched_user && email.matched_user === user?.id;
  const answer = lastAssistantMessage(email.messages);
  const progress = email.status === "processing" ? inProgressAssistant(email.messages) : null;
  const writes = collectWrites(email.messages);
  // The owner card mutates through the ARON conversation endpoints, which
  // invalidate ARON caches — refresh OUR queries so the panel updates instantly.
  const refreshPanel = () => {
    void qc.invalidateQueries({ queryKey: aronMailKeys.email(email.id) });
    void qc.invalidateQueries({ queryKey: aronMailKeys.emails });
  };

  return (
    <div className="flex h-full flex-col overflow-y-auto">
      {/* ── Email header ─────────────────────────────────────────────────── */}
      <div className="border-b border-gray-200 px-4 py-3 dark:border-gray-700">
        <div className="flex flex-wrap items-start justify-between gap-2">
          <h2 className="min-w-0 text-base leading-snug font-semibold text-gray-900 dark:text-gray-100">
            {email.subject || "(bez tematu)"}
          </h2>
          <div className="flex shrink-0 items-center gap-1.5">
            <AronMailStatusBadge status={email.status} label={email.status_display} />
            {email.conversation && (
              <AronModelPicker locked lockedValue={email.reasoning_mode} />
            )}
            {email.status === "processing" && (
              <button
                type="button"
                disabled={cancel.isPending}
                onClick={() => cancel.mutate(email.id)}
                title="Zatrzymaj przetwarzanie tej wiadomości"
                className="inline-flex items-center gap-1 border border-gray-300 px-1.5 py-0.5 text-[11px] text-gray-600 transition-colors hover:border-red-400 hover:text-red-600 disabled:opacity-50 dark:border-gray-600 dark:text-gray-300"
              >
                {cancel.isPending ? (
                  <Loader2 size={12} className="animate-spin" />
                ) : (
                  <Ban size={12} />
                )}
                Anuluj
              </button>
            )}
            {REPROCESSABLE.includes(email.status) && (
              <button
                type="button"
                disabled={reprocess.isPending}
                onClick={() => reprocess.mutate(email.id)}
                title="Puść tę wiadomość przez ARON-a jeszcze raz"
                className="inline-flex items-center gap-1 border border-gray-300 px-1.5 py-0.5 text-[11px] text-gray-600 transition-colors hover:border-red-400 hover:text-red-600 disabled:opacity-50 dark:border-gray-600 dark:text-gray-300"
              >
                {reprocess.isPending ? (
                  <Loader2 size={12} className="animate-spin" />
                ) : (
                  <RefreshCw size={12} />
                )}
                Przetwórz ponownie
              </button>
            )}
          </div>
        </div>

        <div className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-gray-500">
          <span className="inline-flex items-center gap-1">
            <Mail size={12} className="text-gray-400" />
            <span className="font-medium text-gray-700 dark:text-gray-200">
              {email.from_name || email.from_email}
            </span>
            {email.from_name && (
              <span className="text-gray-400">&lt;{email.from_email}&gt;</span>
            )}
          </span>
          <span className="font-mono text-[11px] text-gray-400">
            {formatDateTime(email.received_at)}
          </span>
          {email.matched_user_name ? (
            <span
              className="inline-flex items-center gap-1"
              title="ARON pracuje na uprawnieniach tej osoby"
            >
              <User size={12} className="text-gray-400" /> działa jako{" "}
              <span className="font-medium text-gray-700 dark:text-gray-200">
                {email.matched_user_name}
              </span>
            </span>
          ) : (
            <span className="inline-flex items-center gap-1 text-amber-700">
              <User size={12} /> nadawca nierozpoznany
            </span>
          )}
        </div>

        {email.error && (
          <p className="mt-2 border border-red-200 bg-red-50 px-2 py-1 text-xs text-red-700 dark:border-red-900/50 dark:bg-red-950/30 dark:text-red-300">
            {email.error}
          </p>
        )}
      </div>

      {/* ── Email body ───────────────────────────────────────────────────── */}
      <div className="border-b border-gray-200 px-4 py-3 dark:border-gray-700">
        <AronMailBody text={email.body_text} />

        {email.attachments_meta.length > 0 && (
          <div className="mt-3 flex flex-wrap gap-1.5">
            {email.attachments_meta.map((a, i) => (
              <span
                key={i}
                title={
                  a.imported
                    ? "Załącznik przekazany ARON-owi"
                    : `Pominięty — ${a.skipped_reason}`
                }
                className={`inline-flex max-w-[16rem] items-center gap-1 border px-1.5 py-0.5 text-[11px] ${
                  a.imported
                    ? "border-gray-200 text-gray-600 dark:border-gray-700 dark:text-gray-300"
                    : "border-gray-200 text-gray-400 line-through dark:border-gray-700"
                }`}
              >
                <Paperclip size={11} className="shrink-0" />
                <span className="truncate">{a.name}</span>
                {a.imported && <Check size={11} className="shrink-0 text-emerald-600" />}
              </span>
            ))}
          </div>
        )}
      </div>

      {/* ── Live processing progress ─────────────────────────────────────── */}
      {email.status === "processing" && (
        <div className="border-b border-gray-200 px-4 py-3 dark:border-gray-700">
          <div className="mb-2 flex items-center gap-1.5 text-sm font-medium text-gray-800 dark:text-gray-100">
            <span className="inline-flex h-6 w-6 items-center justify-center bg-red-600 text-white">
              <AronWordmark />
            </span>
            ARON przetwarza wiadomość…
            <Loader2 size={13} className="animate-spin text-red-600" />
          </div>
          {progress && progress.tool_steps.length > 0 && (
            <div className="space-y-1">
              {progress.tool_steps.map((s) => (
                <AronToolStep key={s.id} step={s} />
              ))}
            </div>
          )}
          {progress?.thinking && (
            <details className="mt-2 text-xs text-gray-500">
              <summary className="cursor-pointer select-none">Rozumowanie</summary>
              <div className="mt-1 max-h-48 overflow-auto border border-gray-200 bg-gray-50 p-2 whitespace-pre-wrap dark:border-gray-700 dark:bg-gray-800/60">
                {progress.thinking}
              </div>
            </details>
          )}
          {(!progress || (progress.tool_steps.length === 0 && !progress.thinking)) && (
            <p className="text-xs text-gray-500">
              {email.conversation ? "ARON analizuje treść…" : "Przygotowuję rozmowę…"}
            </p>
          )}
        </div>
      )}

      {/* ── ARON's answer ────────────────────────────────────────────────── */}
      {answer && (
        <div className="border-b border-gray-200 px-4 py-3 dark:border-gray-700">
          <div className="mb-2 flex items-center justify-between gap-2">
            <span className="inline-flex items-center gap-1.5 text-sm font-medium text-gray-800 dark:text-gray-100">
              <span className="inline-flex h-6 w-6 items-center justify-center bg-red-600 text-white">
                <AronWordmark />
              </span>
              Odpowiedź ARON
              {Number(email.total_cost_usd) > 0 && (
                <span
                  className="text-[11px] font-normal text-gray-400"
                  title="Łączny koszt tej rozmowy (tokeny Anthropic)"
                >
                  · koszt: $
                  {Number(email.total_cost_usd) >= 0.01
                    ? Number(email.total_cost_usd).toFixed(2)
                    : Number(email.total_cost_usd).toFixed(4)}
                </span>
              )}
            </span>
            {isOwner && email.conversation && (
              <Link
                to={`/aron?c=${email.conversation}`}
                className="inline-flex items-center gap-1 text-xs text-red-600 hover:underline"
              >
                <ExternalLink size={12} /> Otwórz rozmowę w ARON
              </Link>
            )}
          </div>
          <div className="text-sm">
            <AronMarkdown text={answer.text} conversationId={email.conversation} />
          </div>
        </div>
      )}

      {/* ── Proposed writes (the human gate) ─────────────────────────────── */}
      {writes.length > 0 && (
        <div className="space-y-2 px-4 py-3">
          <h3 className="font-mono text-[10px] font-semibold tracking-[0.12em] text-gray-500 uppercase">
            Propozycje zapisu · {writes.length}
          </h3>
          {writes.map((w) =>
            isOwner && email.conversation ? (
              // The owner approves through the standard owner-scoped endpoints.
              <AronWriteProposal
                key={w.id}
                conversationId={email.conversation}
                write={w}
                onResolved={refreshPanel}
              />
            ) : (
              // CEO acting on another user's email — executes as the CEO.
              <AronMailWriteCard
                key={w.id}
                emailId={email.id}
                ownerName={email.matched_user_name}
                write={w}
              />
            ),
          )}
        </div>
      )}
    </div>
  );
}
